import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IRecruitmentRetentionPlacement,
  IRetentionBarChartMonth,
  IQuickviewMetric,
} from 'app/models/MyCandidates/RecruitmentRetention';

export interface IRecruiterOption {
  id: number;
  name: string;
  value: number;
  object: { userId: number; firstName?: string; lastName?: string };
}

export interface IGridFilters {
  discipline: { id: number; name: string; value: string }[];
  specialty: { id: number; name: string; value: string }[];
  states: string;
  candidateStatus: { id: number; name: string; value: string }[];
  rebooked: string;
  bookedOutOfRange: string;
  placementStatus: any[]; // Tree nodes from TREE filter
}

export interface IMyCandidatesState {
  recruiterId: number | null;
  placements: IRecruitmentRetentionPlacement[];
  barChartData: IRetentionBarChartMonth[];
  quickviewMetrics: IQuickviewMetric[];
  selectedQuickviewKey: string | null;
  selectedBarChartMonth: string | null;
  gridFilters: IGridFilters;
  recruiterOptions: IRecruiterOption[];
  dataLoaded: boolean;
}

export const defaultGridFilters: IGridFilters = {
  discipline: [],
  specialty: [],
  states: 'all',
  candidateStatus: [],
  rebooked: '',
  bookedOutOfRange: '',
  placementStatus: [],
};

const initialState: IMyCandidatesState = {
  recruiterId: null,
  placements: [],
  barChartData: [],
  quickviewMetrics: [],
  selectedQuickviewKey: null,
  selectedBarChartMonth: null,
  gridFilters: defaultGridFilters,
  recruiterOptions: [],
  dataLoaded: false,
};

const fetchRecruiterOptionsAction = createAction<{ userId: number }>('FETCH_MY_CANDIDATES_RECRUITER_OPTIONS');

const fetchRecruitmentRetentionAction = createAction<{
  recruiterId: number;
  startDate: string;
  endDate: string;
}>('FETCH_RECRUITMENT_RETENTION');

const recruiterWorkDeskSlice = createSlice({
  name: 'myCandidates',
  initialState,
  reducers: {
    setRecruitmentRetentionData(
      state,
      action: PayloadAction<{
        placements: IRecruitmentRetentionPlacement[];
        barChartData: IRetentionBarChartMonth[];
        quickviewMetrics: IQuickviewMetric[];
      }>,
    ) {
      state.placements = action.payload.placements;
      state.barChartData = action.payload.barChartData;
      state.quickviewMetrics = action.payload.quickviewMetrics;
      state.dataLoaded = true;
    },
    setRecruiterId(state, action: PayloadAction<number | null>) {
      state.recruiterId = action.payload;
    },
    setRecruiterOptions(state, action: PayloadAction<IRecruiterOption[]>) {
      state.recruiterOptions = action.payload;
    },
    setSelectedQuickviewKey(state, action: PayloadAction<string | null>) {
      state.selectedQuickviewKey = action.payload;
    },
    setSelectedBarChartMonth(state, action: PayloadAction<string | null>) {
      state.selectedBarChartMonth = action.payload;
    },
    setGridFilters(state, action: PayloadAction<IGridFilters>) {
      state.gridFilters = action.payload;
    },
    setDataLoaded(state, action: PayloadAction<boolean>) {
      state.dataLoaded = action.payload;
    },
    resetRecruitmentRetentionFetch(state) {
      state.placements = [];
      state.barChartData = [];
      state.quickviewMetrics = [];
      state.dataLoaded = false;
    },
    reset() {
      return { ...initialState };
    },
  },
});

const { actions } = recruiterWorkDeskSlice;
export const {
  reducer: myCandidatesReducer,
  name: myCandidatesSliceKey,
} = recruiterWorkDeskSlice;
export const myCandidatesActions = {
  ...actions,
  fetchRecruiterOptionsAction,
  fetchRecruitmentRetentionAction,
};
